import { PayloadAction } from '@reduxjs/toolkit';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { put, select, takeLatest } from 'redux-saga/effects';
import { DocumentsAPI } from 'api/document/document-core';
import {
  selectActiveDocumentInfo,
  selectTaskSectionIdNameMap,
  setTaskSectionNameIdMap,
  updateDocTaskIdNameMap,
  selectDocumentResourceSections,
  ResourceSectionType,
} from 'data/documents';
import { Task } from 'data/tasks';

export interface TaskSectionMeta {
  resourceSectionId: number;
  resourceSectionName: string;
  resourceObjects: Task[];
  resourceSectionType: ResourceSectionType.DocumentTask;
}

function* updateDocTaskIdNameMapManager(
  action: PayloadAction<{
    documentId: number;
    projectId: number;
    resourceSectionId: number;
    resourceSectionName?: string;
    resourceObjects?: Task[];
  }>,
) {
  const { payload } = action;

  try {
    const documentResources = yield select(selectDocumentResourceSections);
    const currentMap = yield select(selectTaskSectionIdNameMap);
    const { publicSection } = yield select(selectActiveDocumentInfo);
    const currentMapArray = Object.keys(currentMap).reduce((acc, id) => {
      if (currentMap[id]?.parentDoc === payload.documentId) {
        acc.push({
          resourceSectionId: parseInt(id, 10),
          resourceSectionName: currentMap[id]?.content,
          resourceObjects: currentMap[id]?.resourceObjects ?? [],
          resourceSectionType: ResourceSectionType.DocumentTask,
        });
      }

      return acc;
    }, [] as TaskSectionMeta[]);

    const currentSection = currentMapArray.find(
      (section) => section.resourceSectionId === payload.resourceSectionId,
    );

    const uniqMap = uniqBy(
      [
        {
          resourceSectionId: payload.resourceSectionId,
          resourceSectionName:
            payload.resourceSectionName || currentSection?.resourceSectionName,
          resourceSectionType: ResourceSectionType.DocumentTask,
          resourceObjects: uniqBy(
            [
              ...(payload.resourceObjects ?? []),
              ...(currentSection?.resourceObjects ?? []),
            ],
            'taskId',
          ),
        },
        ...currentMapArray,
        ...documentResources,
      ],
      'resourceSectionId',
    );

    const response = yield DocumentsAPI.updateDocumentTaskSectionsMeta(
      payload.projectId,
      payload.documentId,
      publicSection,
      uniqMap,
    );

    const resourceSections = get(
      response.sections.find(
        (section) => section.documentSectionId === publicSection.id,
      ),
      ['meta', 'resourceSections'],
      [],
    );

    const updatedMap = resourceSections.reduce((acc, item) => {
      if (
        item.resourceSectionId &&
        item?.resourceSectionType === ResourceSectionType.DocumentTask // we should put only the doc sections in the idMap, not the status update resource sections
      ) {
        acc[item.resourceSectionId] = {
          content: item.resourceSectionName,
          parentDoc: payload.documentId,
          resourceSectionType: ResourceSectionType.DocumentTask,
          resourceObjects: item.resourceObjects,
        };
      }

      return acc;
    }, {});

    if (!isEmpty(updatedMap)) {
      yield put(setTaskSectionNameIdMap(updatedMap));
    }
  } catch {
    // TODO
  }
}
export const watchUpdateDocTaskIdNameMap = function* () {
  yield takeLatest(updateDocTaskIdNameMap.type, updateDocTaskIdNameMapManager);
};
